import { OidcAccessToken } from "@axa-fr/react-oidc/dist/ReactOidc";

export interface ImpersonationRequest {
  target_user_id: string;
  target_user_email?: string;
  target_user_name?: string;
  target_user_groups?: string[];
}

export interface ImpersonationResponse {
  success: boolean;
  message: string;
  impersonated_user?: any;
  original_user?: any;
}

export interface TestUser {
  target_user_id: string;
  target_user_email?: string;
  target_user_name?: string;
  target_user_groups?: string[];
  description: string;
}

export interface TestUsersResponse {
  test_users: TestUser[];
  note: string;
}

/**
 * Start impersonating another user
 */
export async function startImpersonation(
  accessToken: OidcAccessToken,
  request: ImpersonationRequest,
): Promise<ImpersonationResponse> {
  const host: string = process.env.REACT_APP_LLMGATEWAY_HOST ?? "";
  const headers: HeadersInit = {
    Authorization: `Bearer ${accessToken.accessToken}`,
    "Content-Type": "application/json",
  };

  const resp = await fetch(`${host}/impersonation/start`, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(request),
  });

  if (!resp.ok) {
    const errorText = await resp.text();
    throw new Error(`HTTP ${resp.status}: ${resp.statusText}${errorText ? ` - ${errorText}` : ''}`);
  }

  return await resp.json();
}

/**
 * Stop current impersonation session
 */
export async function stopImpersonation(
  accessToken: OidcAccessToken,
): Promise<ImpersonationResponse> {
  const host: string = process.env.REACT_APP_LLMGATEWAY_HOST ?? "";
  const headers: HeadersInit = {
    Authorization: `Bearer ${accessToken.accessToken}`,
    "Content-Type": "application/json",
  };

  const resp = await fetch(`${host}/impersonation/stop`, {
    method: "POST",
    headers: headers,
  });

  if (!resp.ok) {
    const errorText = await resp.text();
    throw new Error(`HTTP ${resp.status}: ${resp.statusText}${errorText ? ` - ${errorText}` : ''}`);
  }

  return await resp.json();
}

/**
 * Get current impersonation status
 */
export async function getImpersonationStatus(
  accessToken: OidcAccessToken,
): Promise<any> {
  const host: string = process.env.REACT_APP_LLMGATEWAY_HOST ?? "";
  const headers: HeadersInit = {
    Authorization: `Bearer ${accessToken.accessToken}`,
    "Content-Type": "application/json",
  };

  const resp = await fetch(`${host}/impersonation/status`, {
    method: "GET",
    headers: headers,
  });

  if (!resp.ok) {
    const errorText = await resp.text();
    throw new Error(`HTTP ${resp.status}: ${resp.statusText}${errorText ? ` - ${errorText}` : ''}`);
  }

  return await resp.json();
}

/**
 * Get list of test users for impersonation
 */
export async function getTestUsers(
  accessToken: OidcAccessToken,
): Promise<TestUsersResponse> {
  const host: string = process.env.REACT_APP_LLMGATEWAY_HOST ?? "";
  const headers: HeadersInit = {
    Authorization: `Bearer ${accessToken.accessToken}`,
    "Content-Type": "application/json",
  };

  const resp = await fetch(`${host}/impersonation/test-users`, {
    method: "GET",
    headers: headers,
  });

  if (!resp.ok) {
    const errorText = await resp.text();
    throw new Error(`HTTP ${resp.status}: ${resp.statusText}${errorText ? ` - ${errorText}` : ''}`);
  }

  return await resp.json();
} 